import { orderBy, uniqueId } from 'lodash';

import type { AssetType } from 'src/data/asset';
import type { BenchmarkConfig } from 'src/data/benchmark';
import type {
  AiRecomposeMode,
  AiSuggestionMode,
  ContentValidation,
  GenerationGuidance,
  GenerationRequest,
  Language,
} from 'src/data/generation';
import {
  GENERATION_TOOL,
  type GenerationTool,
  isDataDrivenGenerationTool,
  isLpcoGenerationTool,
  isMixtureTool,
  isNoScoreTool,
  isScorableBlogTool,
} from 'src/data/generationTool';
import type { ImageOverlayProps } from 'src/data/image';
import type { ScoringMethod } from 'src/data/scoring';
import type { LandingPageAsset } from 'src/data/wtm';
import type { Entity, ID } from 'src/lib/collection';
import { parseJson } from 'src/lib/common';
import {
  deserializeSlateMarkedValue,
  serializeSlateMarkedValue,
  type SlateValue,
  type TextAndMarkup,
} from 'src/lib/slate';

export interface Variation extends Entity {
  source: string;
  generationTool: GenerationTool;
  assetType: AssetType;
  copyText: string;
  copyTextMarkup?: string;
  status: VariationStatus;
  score: number;
  createdAt: string;
  lastUpdateTime: string;
  ageScoreHistogram?: string;
  genderScoreHistogram?: string;
  contentSafety?: ContentValidation;
  engagementScoreSupportSize?: number;
  landingPageAsset?: LandingPageAsset;
  language?: Language;
  overrideLanguage?: Language;
  generationGuidance?: GenerationGuidance;
  mixtureSubAssetType?: AssetType;
  groupIdx: number[];
  groupOrderIdx?: number[];
  projectAiSuggestionRequestId?: ID;
  projectAiRecomposeRequestId?: ID;
  originalVariationId?: ID;
  chainId?: ID;
  csvChainId?: ID;
  blockId?: ID;
  chainFields?: string;
  multiPromptStepIndex?: ID;
  multiPromptStepName?: string;
  customMultiPromptId?: ID;
  multiPromptTool?: GenerationTool;
  imageBlob?: string;
  imageMeta?: string; // JSON string
  imagePath?: string;
  imageStyleId?: ID;
  overlayText?: ImageOverlayProps;
  significantImproveOrigin?: boolean;
  suggestionMode?: AiSuggestionMode;
  recomposeMode?: AiRecomposeMode;
  isScoreLocked?: boolean;
  performancePredictionsScoreLocked?: boolean;
  isEdited?: boolean;
  isRecompose?: boolean;
  isEnterpriseScoreUsed?: boolean;
  scoringMethod: ScoringMethod;
  secondaryScoringMethod?: ScoringMethod;
  classifiedGenerationTool?: GenerationTool;
  benchmarkConfig?: BenchmarkConfig;
  rating?: string;
  exportInfo?: object;
  customFormatIds?: ID[];
  complianceStatus?: string; // TODO: change to ComplianceStatus when available
  complianceProgressPercentage?: number;

  variations?: Variation[]; // used only on FE to track nested variations
  originalGenerationTool?: GenerationTool; // used only on FE to track the original generation tool if the variation is a result of a multi-prompt
}

export const VARIATION_STATUS = {
  draft: 'draft',
  saved: 'saved',
  discarded: 'discarded',
  archived: 'archived',
} as const;
export type VariationStatus = (typeof VARIATION_STATUS)[keyof typeof VARIATION_STATUS];

export const isVariationArchived = (variation: Variation): boolean =>
  variation.status === VARIATION_STATUS.archived;

export const VARIATION_SOURCE = {
  suggest: 'suggest',
  recompose: 'recompose',
  custom: 'custom',
  defaultSuggest: 'default_suggest',
} as const;
export type VariationSource = (typeof VARIATION_SOURCE)[keyof typeof VARIATION_SOURCE];

export const BLOG_VARIATION_INTRO_SECTION_ID = 'section_intro';

export const VARIATION_RATING = {
  thumbsUp: 'thumbs_up',
  thumbsDown: 'thumbs_down',
  none: 'none',
} as const;
export type VariationRating = (typeof VARIATION_RATING)[keyof typeof VARIATION_RATING];

export const variationRatingTextToValue = (text: VariationRating): 0 | 1 | -1 => {
  switch (text) {
    case VARIATION_RATING.thumbsUp:
      return 1;
    case VARIATION_RATING.thumbsDown:
      return -1;
    default:
      return 0;
  }
};

export function sortVariationsByScore(variations: Variation[]): Variation[] {
  return orderBy(variations, ['assetType', 'score', 'id'], ['desc', 'desc', 'asc']);
}

export function sortVariations(variations: Variation[]): Variation[] {
  return sortVariationsByScore(variations);
}

export function sortSavedVariations(variations: Variation[]): Variation[] {
  return orderBy(variations, ['lastUpdateTime'], ['desc']);
}

export const variationHasEmptyContent = (copyText: string): boolean => {
  const alignedText = copyText || '';
  return alignedText.trim().length === 0;
};

export const parseVariationSlateContent = (rawContent: TextAndMarkup): SlateValue => {
  return deserializeSlateMarkedValue(rawContent);
};

export const serializeVariationSlateContent = (data: SlateValue): TextAndMarkup => {
  return serializeSlateMarkedValue(data);
};

interface HistogramData {
  ages?: HistogramRow[];
  gender?: HistogramObject;
  genders?: HistogramRow[];
}

type HistogramObject = Record<string, number>;
type HistogramRow = { name: string; value: number };

const histogramObjectToArray = (obj: HistogramObject): HistogramRow[] => {
  const array = Object.entries(obj).map(([name, value]) => ({ name, value }));
  return orderBy(array, ['name'], ['asc']);
};

export const parseHistogramData = (variation: Variation): HistogramData | null => {
  const histogramData: HistogramData = {};

  if (variation?.ageScoreHistogram) {
    const ages = parseJson(variation.ageScoreHistogram) as HistogramObject;
    histogramData.ages = histogramObjectToArray(ages);
  }
  if (variation?.genderScoreHistogram) {
    const genderData = parseJson(variation.genderScoreHistogram) as HistogramObject;
    histogramData.gender = genderData;
    histogramData.genders = histogramObjectToArray(genderData);
  }

  return Object.keys(histogramData).length ? histogramData : null;
};

export const addLanguageToVariations = (variations: Variation[], language: Language): Variation[] =>
  variations.map((variation) => ({ ...variation, language }));

export const loopOverVariations = (variations: Variation[], callback: Function): void => {
  for (const variation of variations) {
    callback(variation);
    if (variation.variations) {
      loopOverVariations(variation.variations, callback);
    }
  }
};

export const addLanguageToGenerationResult = (result: GenerationRequest): GenerationRequest => {
  const language = result.language;
  const resultClone = { ...result };

  loopOverVariations(resultClone.variations, (variation: Variation) => {
    variation.language = variation.overrideLanguage || language;
  });

  return resultClone;
};

export const addLanguageToGenerationResults = (results: GenerationRequest[]): GenerationRequest[] =>
  results.map(addLanguageToGenerationResult);

export const generateVariationInstanceId = (variationId: ID): string =>
  `${variationId}_${uniqueId()}`;
export const getVariationIdFromInstanceId = (instanceId: string): ID => instanceId?.split('_')[0];

export const flattenAndMergeVariations = (
  variations: Variation[],
  addonParams: Partial<Variation>
): Variation[] => {
  const result: Variation[] = [];
  for (let variation of variations) {
    if (addonParams) {
      variation = { ...variation, ...addonParams };
    }

    // if variation already exists in the result, skip it
    if (result.find((v) => v.id === variation.id)) {
      continue;
    }

    result.push(variation);
    const extraParams = { ...addonParams };
    if (!variation.originalGenerationTool) {
      extraParams.originalGenerationTool = variation.generationTool;
    } else {
      extraParams.originalGenerationTool = variation.originalGenerationTool;
    }
    if (variation.customMultiPromptId) {
      extraParams.customMultiPromptId = variation.customMultiPromptId;
    }
    if (variation.multiPromptTool) {
      extraParams.multiPromptTool = variation.multiPromptTool;
    }
    if (variation.multiPromptStepIndex) {
      extraParams.multiPromptStepIndex = variation.multiPromptStepIndex;
    }
    if (variation.multiPromptStepName) {
      extraParams.multiPromptStepName = variation.multiPromptStepName;
    }
    if (variation.mixtureSubAssetType) {
      extraParams.mixtureSubAssetType = variation.mixtureSubAssetType;
    }

    if (variation?.variations) {
      result.push(...flattenAndMergeVariations(variation?.variations, extraParams));
    }
  }
  return result;
};

export const variationIsRecomposeOriginal = ({
  recomposeMode,
  originalVariationId,
}: {
  recomposeMode: AiRecomposeMode;
  originalVariationId: ID;
}): boolean => {
  // it might be not a recompose variation at all
  if (!recomposeMode) {
    return false;
  }

  // If we have "originalVariationId", then it's not an original variation, but a child of it
  return !originalVariationId;
};

export const getVariationMixtureAssetType = (variation: Variation): AssetType | null => {
  if (variation.mixtureSubAssetType) {
    return variation.mixtureSubAssetType;
  }
  if (isMixtureTool(variation.generationTool)) {
    return variation.assetType;
  }

  return null;
};

export const HIDE_NON_ENGLISH_SCORE_CUTOFF_DATE = '2024-09-24';

const GENERAL_PRODUCT_LISTING_SCORE_CUTOFF_DATE = '2024-11-14';

export const shouldVariationScoreBeHidden = (
  generationTool: GenerationTool,
  lastUpdateTime: string
): boolean => {
  return (
    generationTool === GENERATION_TOOL.generalProductListing &&
    lastUpdateTime < GENERAL_PRODUCT_LISTING_SCORE_CUTOFF_DATE
  );
};

export const isVariationScorable = (
  generationTool: GenerationTool,
  lastUpdateTime: string
): boolean => {
  return (
    isNoScoreTool(generationTool) ||
    shouldVariationScoreBeHidden(generationTool, lastUpdateTime) ||
    // some tools are score-able even though it's dd
    (!isDataDrivenGenerationTool(generationTool) &&
      !isScorableBlogTool(generationTool) &&
      !isLpcoGenerationTool(generationTool))
  );
};

export const isDummyVariationId = (id: ID): boolean => parseInt(String(id)) < 0;

export const isSuggestionVariation = (variation: Variation) => {
  return variation.projectAiSuggestionRequestId != null;
};

export const isRecomposeVariation = (variation: Variation) => {
  return variation.projectAiRecomposeRequestId != null;
};

export const isSectionRecomposeVariation = (variation: Variation) => {
  return isRecomposeVariation(variation) && variation.chainId != null;
};
